import { useEffect, useState } from "react";
import Layout from "@/components/Layout";
import { api, formatError, WASTE_TYPES, wasteLabel } from "@/lib/api";
import { Card, CardContent } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Switch } from "@/components/ui/switch";
import { Select, SelectTrigger, SelectValue, SelectContent, SelectItem } from "@/components/ui/select";
import { Checkbox } from "@/components/ui/checkbox";
import { Sparkles, Recycle, LayoutGrid, Save, Loader2, KeyRound, Eye, EyeOff } from "lucide-react";
import { toast } from "sonner";

const PROVIDER_OPTIONS = [
  { value: "emergent", label: "Emergent (Gemini via chiave universale)" },
  { value: "openai", label: "OpenAI (chiave propria)" },
  { value: "anthropic", label: "Anthropic (chiave propria)" },
];

const MODEL_SUGGESTIONS = {
  emergent: ["gemini-3-flash-preview", "gemini-2.5-flash", "gemini-2.5-pro"],
  openai: ["gpt-4o", "gpt-4o-mini", "gpt-4.1-mini"],
  anthropic: ["claude-sonnet-4-5-20250929", "claude-3-5-sonnet-latest", "claude-3-5-haiku-latest"],
};

export default function AdminSettingsPage() {
  const [loading, setLoading] = useState(true);
  const [busy, setBusy] = useState(false);
  const [all, setAll] = useState([]);
  const [ai, setAi] = useState({ enabled: false, provider: "emergent", model: "", api_key: "", min_confidence: 0.75, api_key_masked: false });
  const [wasteEnabled, setWasteEnabled] = useState([]);
  const [layout, setLayout] = useState({ columns: 2, rows: 4, header_mm: 30, footer_mm: 15 });
  const [showKey, setShowKey] = useState(false);
  const [apiKeyDirty, setApiKeyDirty] = useState(false);

  const load = async () => {
    setLoading(true);
    try {
      const { data } = await api.get("/admin/settings");
      setAll(data.all_waste_types || []);
      setAi({ ...data.ai_vision, api_key: data.ai_vision.api_key || "" });
      setApiKeyDirty(false);
      setWasteEnabled(data.waste_types_enabled || []);
      setLayout(data.ream_layout || { columns: 2, rows: 4, header_mm: 30, footer_mm: 15 });
    } catch (e) { toast.error(formatError(e)); }
    finally { setLoading(false); }
  };
  useEffect(() => { load(); }, []);

  const toggleWaste = (v) => {
    setWasteEnabled((cur) => cur.includes(v) ? cur.filter((x) => x !== v) : [...cur, v]);
  };

  const save = async () => {
    setBusy(true);
    try {
      const payload = {
        ai_vision: {
          enabled: !!ai.enabled,
          provider: ai.provider,
          model: ai.model,
          min_confidence: parseFloat(ai.min_confidence),
        },
        waste_types_enabled: wasteEnabled,
        ream_layout: {
          columns: parseInt(layout.columns),
          rows: parseInt(layout.rows),
          header_mm: parseFloat(layout.header_mm),
          footer_mm: parseFloat(layout.footer_mm),
        },
      };
      // Only send api_key if the user has actually edited it (avoid overwriting with masked value)
      if (apiKeyDirty && ai.api_key) payload.ai_vision.api_key = ai.api_key;
      await api.patch("/admin/settings", payload);
      toast.success("Impostazioni salvate — attive immediatamente");
      await load();
    } catch (e) { toast.error(formatError(e)); }
    finally { setBusy(false); }
  };

  if (loading) return <Layout title="Impostazioni"><div className="text-sm text-muted-foreground">Caricamento…</div></Layout>;

  const modelSuggestions = MODEL_SUGGESTIONS[ai.provider] || [];
  const totalPerSheet = (parseInt(layout.columns) || 0) * (parseInt(layout.rows) || 0);

  return (
    <Layout
      title="Impostazioni"
      subtitle="Configurazione runtime del servizio. Le modifiche sono attive immediatamente su preview e produzione."
      actions={<Button onClick={save} disabled={busy} className="rounded-full gap-2 h-11 px-5" data-testid="settings-save"><Save className="w-4 h-4" />{busy ? <Loader2 className="w-4 h-4 animate-spin" /> : "Salva impostazioni"}</Button>}
    >
      <div className="space-y-6 max-w-4xl">
        {/* AI Vision */}
        <Card data-testid="settings-ai-card">
          <CardContent className="p-6 space-y-5">
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-3">
                <div className="w-10 h-10 rounded-lg bg-purple-100 text-purple-700 flex items-center justify-center"><Sparkles className="w-5 h-5" /></div>
                <div>
                  <h2 className="font-display text-lg font-semibold">Lettura AI foto (OCR peso + barcode)</h2>
                  <p className="text-xs text-muted-foreground">Se disabilitata, tutti i dati saranno inseriti manualmente dall'operatore.</p>
                </div>
              </div>
              <Switch checked={ai.enabled} onCheckedChange={(v) => setAi({ ...ai, enabled: v })} data-testid="ai-enabled-switch" />
            </div>

            {ai.enabled && (
              <div className="pt-2 border-t border-gray-200 space-y-4">
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div>
                    <Label className="text-xs uppercase tracking-wider text-gray-600">Provider</Label>
                    <Select value={ai.provider} onValueChange={(v) => setAi({ ...ai, provider: v, model: MODEL_SUGGESTIONS[v]?.[0] || ai.model })}>
                      <SelectTrigger data-testid="ai-provider-select" className="mt-1.5"><SelectValue /></SelectTrigger>
                      <SelectContent>
                        {PROVIDER_OPTIONS.map((p) => <SelectItem key={p.value} value={p.value} data-testid={`ai-provider-opt-${p.value}`}>{p.label}</SelectItem>)}
                      </SelectContent>
                    </Select>
                  </div>
                  <div>
                    <Label className="text-xs uppercase tracking-wider text-gray-600">Modello</Label>
                    <Input value={ai.model} onChange={(e) => setAi({ ...ai, model: e.target.value })} placeholder={modelSuggestions[0]} className="mt-1.5 font-mono text-sm" data-testid="ai-model-input" />
                    <div className="text-xs text-muted-foreground mt-1.5 flex flex-wrap gap-1">
                      {modelSuggestions.map((m) => (
                        <button key={m} type="button" onClick={() => setAi({ ...ai, model: m })} className="px-2 py-0.5 border border-gray-200 rounded hover:bg-gray-50 font-mono text-[10px]" data-testid={`ai-model-suggest-${m}`}>{m}</button>
                      ))}
                    </div>
                  </div>
                </div>

                {ai.provider !== "emergent" && (
                  <div>
                    <Label className="text-xs uppercase tracking-wider text-gray-600 flex items-center gap-1"><KeyRound className="w-3.5 h-3.5" /> API key {ai.provider === "openai" ? "OpenAI" : "Anthropic"}</Label>
                    <div className="relative mt-1.5">
                      <Input
                        type={showKey ? "text" : "password"}
                        value={ai.api_key}
                        onChange={(e) => { setAi({ ...ai, api_key: e.target.value }); setApiKeyDirty(true); }}
                        placeholder={ai.api_key_masked && !apiKeyDirty ? "Lascia vuoto per mantenere la chiave attuale" : `${ai.provider === "openai" ? "sk-…" : "sk-ant-…"}`}
                        className="font-mono text-sm pr-10"
                        data-testid="ai-apikey-input"
                      />
                      <button type="button" onClick={() => setShowKey(!showKey)} className="absolute right-2 top-1/2 -translate-y-1/2 text-gray-500 hover:text-gray-700" data-testid="ai-apikey-toggle">
                        {showKey ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
                      </button>
                    </div>
                    <div className="text-xs text-muted-foreground mt-1.5">
                      La chiave viene salvata cifrata sul server. Con provider "Emergent" non serve chiave: viene usata quella universale del Comune.
                    </div>
                  </div>
                )}

                <div>
                  <Label className="text-xs uppercase tracking-wider text-gray-600">Soglia di confidence (0.0 – 1.0)</Label>
                  <Input type="number" step="0.05" min="0" max="1" value={ai.min_confidence} onChange={(e) => setAi({ ...ai, min_confidence: e.target.value })} className="mt-1.5 max-w-[200px]" data-testid="ai-minconf-input" />
                  <div className="text-xs text-muted-foreground mt-1.5">Sotto questa soglia, il valore letto dall'AI non viene applicato — decide l'operatore.</div>
                </div>
              </div>
            )}
          </CardContent>
        </Card>

        {/* Waste types */}
        <Card data-testid="settings-waste-card">
          <CardContent className="p-6">
            <div className="flex items-center gap-3 mb-4">
              <div className="w-10 h-10 rounded-lg bg-emerald-100 text-emerald-700 flex items-center justify-center"><Recycle className="w-5 h-5" /></div>
              <div>
                <h2 className="font-display text-lg font-semibold">Tipologie di rifiuto conferibili</h2>
                <p className="text-xs text-muted-foreground">Solo le tipologie selezionate saranno disponibili al cittadino.</p>
              </div>
            </div>
            <div className="grid grid-cols-2 md:grid-cols-3 gap-3">
              {(all.length ? all : WASTE_TYPES.map((w) => w.value)).map((v) => {
                const color = WASTE_TYPES.find((w) => w.value === v)?.color || "#6b7280";
                const label = wasteLabel(v);
                const on = wasteEnabled.includes(v);
                return (
                  <label key={v} className={`flex items-center gap-3 p-3 rounded-lg border cursor-pointer transition-colors ${on ? "border-primary/40 bg-primary/5" : "border-gray-200 bg-white hover:bg-gray-50"}`}>
                    <Checkbox checked={on} onCheckedChange={() => toggleWaste(v)} data-testid={`waste-toggle-${v}`} />
                    <span className="w-2.5 h-2.5 rounded-full" style={{ background: color }} />
                    <span className="text-sm font-medium">{label}</span>
                  </label>
                );
              })}
            </div>
            {wasteEnabled.length === 0 && (
              <div className="mt-3 p-3 bg-amber-50 border border-amber-200 rounded-lg text-sm text-amber-900">
                Attenzione: nessuna tipologia abilitata. I cittadini non potranno effettuare conferimenti.
              </div>
            )}
          </CardContent>
        </Card>

        {/* Ream layout */}
        <Card data-testid="settings-layout-card">
          <CardContent className="p-6">
            <div className="flex items-center gap-3 mb-4">
              <div className="w-10 h-10 rounded-lg bg-blue-100 text-blue-700 flex items-center justify-center"><LayoutGrid className="w-5 h-5" /></div>
              <div>
                <h2 className="font-display text-lg font-semibold">Layout PDF risme etichette</h2>
                <p className="text-xs text-muted-foreground">Griglia per foglio A4. Tipicamente 2 colonne × 4 righe = 8 etichette per foglio.</p>
              </div>
            </div>
            <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
              <div><Label className="text-xs uppercase tracking-wider text-gray-600">Colonne</Label><Input type="number" min="1" max="6" value={layout.columns} onChange={(e) => setLayout({ ...layout, columns: e.target.value })} className="mt-1.5" data-testid="layout-cols" /></div>
              <div><Label className="text-xs uppercase tracking-wider text-gray-600">Righe</Label><Input type="number" min="1" max="12" value={layout.rows} onChange={(e) => setLayout({ ...layout, rows: e.target.value })} className="mt-1.5" data-testid="layout-rows" /></div>
              <div><Label className="text-xs uppercase tracking-wider text-gray-600">Header (mm)</Label><Input type="number" min="0" max="100" step="1" value={layout.header_mm} onChange={(e) => setLayout({ ...layout, header_mm: e.target.value })} className="mt-1.5" data-testid="layout-header" /></div>
              <div><Label className="text-xs uppercase tracking-wider text-gray-600">Footer (mm)</Label><Input type="number" min="0" max="100" step="1" value={layout.footer_mm} onChange={(e) => setLayout({ ...layout, footer_mm: e.target.value })} className="mt-1.5" data-testid="layout-footer" /></div>
            </div>
            <div className="mt-4 p-3 bg-blue-50 border border-blue-100 rounded-lg text-sm text-blue-900" data-testid="layout-preview">
              Configurazione attuale: <strong>{totalPerSheet}</strong> etichette per foglio A4 ({layout.columns} × {layout.rows}). Header {layout.header_mm} mm, footer {layout.footer_mm} mm.
            </div>
          </CardContent>
        </Card>
      </div>
    </Layout>
  );
}
